import React, { createContext, useContext, useEffect, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import { rollbar } from "src/utils/ErrorBoundary/index.js";
import { auth } from "src/firebase/firebase.js";

export let isStripeLoaded = false;
export let isStripeLoading = true;

export const StripeLoadContext = createContext({
  isLoading: true,
  isLoaded: false,
  isFailed: false,
});

export const useStripeLoadStatus = () => useContext(StripeLoadContext);

const loadStripeWithRetry = async (retries = 3, delay = 3000) => {
  let lastError = null;

  for (let attempt = 0; attempt < retries; attempt++) {
    try {
      const stripeInstance = await loadStripe(
        process.env.REACT_APP_STRIPE_PUBLIC_KEY
      );

      if (stripeInstance) {
        isStripeLoaded = true;
        isStripeLoading = false;
        return stripeInstance;
      }

      lastError = new Error("Stripe.js returned no instance");
    } catch (error) {
      console.error(
        `Failed to load Stripe.js (attempt ${attempt + 1} of ${retries})`,
        error
      );
      lastError = error;
    }

    if (attempt < retries - 1) {
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }

  isStripeLoading = false;
  console.error("Failed to load Stripe.js after multiple attempts");
  const userSignedIn = Boolean(auth.currentUser);
  rollbar.error("Failed to load Stripe.js after multiple attempts", lastError, {
    userSignedIn,
  });
  return null;
};

// Keep one stable promise for the lifetime of the app. Elements can consume a
// Promise<Stripe | null>, so the app can render while Stripe.js loads.
const stripePromise = loadStripeWithRetry();

const StripeWrapper = ({ children }) => {
  const [status, setStatus] = useState({
    isLoading: isStripeLoading,
    isLoaded: isStripeLoaded,
    isFailed: !isStripeLoading && !isStripeLoaded,
  });

  useEffect(() => {
    let isMounted = true;
    stripePromise.then(() => {
      if (!isMounted) return;
      setStatus({
        isLoading: isStripeLoading,
        isLoaded: isStripeLoaded,
        isFailed: !isStripeLoading && !isStripeLoaded,
      });
    });

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <StripeLoadContext.Provider value={status}>
      <Elements stripe={stripePromise}>{children}</Elements>
    </StripeLoadContext.Provider>
  );
};

export default StripeWrapper;
